// Client HTTP verso l'API KPO.
//
// Unico punto in cui si parla di rete: le schermate chiamano funzioni tipizzate e ricevono
// dati o un errore già interpretato. In particolare distingue tre casi che l'utente deve
// poter capire in modo diverso:
//   - 422: l'operazione non è consentita (minimo d'ordine, permessi, regola di business);
//   - 503: il gestionale non è raggiungibile, quindi è un problema temporaneo;
//   - il resto: un guasto, da segnalare come tale.

const BASE = import.meta.env.VITE_API_BASE ?? '/api/v1'

/** Utente impersonato in sviluppo. In produzione al suo posto c'è il token. */
const DEV_USER = import.meta.env.VITE_DEV_USER ?? 'operatore'

/**
 * Sessione embed (ingresso dei negozi da KBE): il token arriva nel FRAMMENTO dell'URL — che
 * non transita dal server — si mette al riparo in sessionStorage e da lì viaggia in un header
 * a ogni chiamata. Niente cookie: dentro l'iframe di KBE sarebbero di terza parte e bloccati.
 */
const EMBED_KEY = 'kpo-embed'
{
  const match = /[#&]et=([^&]+)/.exec(window.location.hash)
  if (match) {
    sessionStorage.setItem(EMBED_KEY, decodeURIComponent(match[1]))
    history.replaceState(null, '', window.location.pathname + window.location.search)
  }
}

/** Vero quando si è entrati da KBE: la pagina vive in un iframe e non ha un login da chiudere. */
export const isEmbed = () => sessionStorage.getItem(EMBED_KEY) !== null

export type ProblemKind = 'validation' | 'unavailable' | 'notFound' | 'forbidden' | 'error'

export class ApiError extends Error {
  // Campi dichiarati nel corpo e assegnati nel costruttore: il progetto compila con
  // `erasableSyntaxOnly`, che vieta le proprietà dichiarate fra i parametri.
  readonly kind: ProblemKind
  readonly title: string
  readonly detail: string
  readonly status: number

  constructor(kind: ProblemKind, title: string, detail: string, status: number) {
    super(detail || title)
    this.kind = kind
    this.title = title
    this.detail = detail
    this.status = status
  }

  /** Vero quando ha senso proporre "riprova": la causa è esterna e transitoria. */
  get retryable() {
    return this.kind === 'unavailable'
  }
}

type Query = Record<string, string | number | boolean | undefined | null | string[]>

function buildUrl(path: string, query?: Query) {
  const url = new URL(BASE + path, window.location.origin)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue
      if (Array.isArray(value)) value.forEach((v) => url.searchParams.append(key, v))
      else url.searchParams.append(key, String(value))
    }
  }
  return url.pathname + url.search
}

async function toError(response: Response): Promise<ApiError> {
  let title = response.statusText
  let detail = ''

  try {
    const body = await response.json()
    title = body.title ?? title
    detail = body.detail ?? ''
  } catch {
    /* corpo non JSON: restano statusText e dettaglio vuoto */
  }

  const kind: ProblemKind =
    response.status === 422 ? 'validation'
    : response.status === 503 ? 'unavailable'
    : response.status === 404 ? 'notFound'
    : response.status === 403 || response.status === 401 ? 'forbidden'
    : 'error'

  return new ApiError(kind, title, detail, response.status)
}

async function request<T>(method: string, path: string, options: { query?: Query; body?: unknown } = {}): Promise<T> {
  const headers: Record<string, string> = { 'X-Dev-User': DEV_USER }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'

  const embed = sessionStorage.getItem(EMBED_KEY)
  if (embed) headers['X-Embed-Token'] = embed

  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  })

  if (!response.ok) throw await toError(response)
  if (response.status === 204) return undefined as T

  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown, query?: Query) => request<T>('POST', path, { body, query }),
  put: <T>(path: string, body?: unknown, query?: Query) => request<T>('PUT', path, { body, query }),
  patch: <T>(path: string, body?: unknown, query?: Query) => request<T>('PATCH', path, { body, query }),
  del: <T>(path: string, query?: Query) => request<T>('DELETE', path, { query }),

  /** Caricamento file: non passa da JSON.stringify. */
  upload: async <T>(path: string, file: File): Promise<T> => {
    const form = new FormData()
    form.append('file', file)

    const headers: Record<string, string> = { 'X-Dev-User': DEV_USER }
    const embed = sessionStorage.getItem(EMBED_KEY)
    if (embed) headers['X-Embed-Token'] = embed

    const response = await fetch(buildUrl(path), {
      method: 'POST',
      headers,
      body: form,
    })

    if (!response.ok) throw await toError(response)
    const text = await response.text()
    return (text ? JSON.parse(text) : undefined) as T
  },

  /** URL diretto, per aprire un allegato in una scheda del browser. */
  url: (path: string) => BASE + path,
}
